<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null"
          @click="showCreateForm()"
        >
          Create New Booking
        </b-button>
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field label="Start Date">
              <b-datepicker
                v-model="start"
                name="startdate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="Start date"
              />
            </b-field>
            <b-field label="End Date">
              <b-datepicker
                v-model="end"
                name="enddate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="End date"
              />
            </b-field>
            <b-field v-if="hasGroupPlants" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" expanded>
                <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
          </b-field>
          <ag-grid-vue
            v-if="bookings.length != 0"
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :row-data="bookings"
            :grid-options="gridOptions"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
          />
        </div>
      </div>
    </div>
    <b-modal :active.sync="isCreatingBooking" scroll="keep" has-modal-card>
      <BookingForm :plant="selectedPlant" @submit="submitBooking" />
    </b-modal>
    <b-modal :active.sync="isEditingBooking" scroll="keep" has-modal-card>
      <form v-if="isEditingBooking" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Movement
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Crate Type">
                <b-select v-model="editableBooking.type" placeholder="Crate Type">
                  <option value="1">
                    1
                  </option>
                  <option value="5.1">
                    5.1
                  </option>
                  <option value="5.2">
                    5.2
                  </option>
                  <option value="5.5">
                    5.5
                  </option>
                  <option value="GPSFood">
                    GPS Food
                  </option>
                  <option value="ZEON">
                    ZEON
                  </option>
                  <option value="RHODIA">
                    RHODIA
                  </option>
                </b-select>
              </b-field>
              <b-field label="Date">
                <b-datepicker
                  v-model="editableBooking.date"
                  name="date"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Date"
                />
              </b-field>
              <b-field label="Title">
                <b-input v-model="editableBooking.title" type="text" />
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Number of Crates">
                <b-input v-model="editableBooking.num_crates" type="number" placeholder="Crates" />
              </b-field>
              <b-field label="Activity">
                <b-button :type="editableBooking.leaving == true ? 'is-success' : 'is-light'" @click="changeLeaving(true)">
                  Delivery/Transfer out
                </b-button>
                <b-button :type="editableBooking.leaving == false ? 'is-success' : 'is-light'" @click="changeLeaving(false)">
                  Collect/Transfer in
                </b-button>
              </b-field>
            </b-field>
            <b-field label="Comment">
              <b-input v-model="editableBooking.comment" type="text" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import { mapState } from 'vuex'
import BookingForm from './bookingForm.vue'
import CrateBookings from '../../services/v3/crateBookings'
import plantSelector from '@/mixins/plantSelector'
import { restoreConsultingPlant } from '@/helpers/plants'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'CrateBooking',
  components: {
    BookingForm
  },
  mixins: [plantSelector],
  data: function () {
    return {
      editableBooking: null,
      start: new Date(),
      end: new Date(),
      selectedPlant: restoreConsultingPlant(),
      isLoading: false,
      isCreatingBooking: false,
      isEditingBooking: false,
      bookings: [],
      modules: AllModules,
      gridOptions: {
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        columnDefs: [
          { headerName: 'Title',
            field: 'title',
            filter: 'agTextColumnFilter',
            width: 180,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Crates',
            field: 'num_crates',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Crate Type',
            field: 'type',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Date',
            field: 'date',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            sort: 'Desc',
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Booking',
            field: 'leaving',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Comment',
            field: 'comment',
            filter: 'agTextColumnFilter',
            width: 180,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          }
        ],
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },
  mounted: async function () {
    await this.loadPlants()
  },
  methods: {
    changeLeaving (leaving) {
      this.editableBooking.leaving = leaving
    },
    resetData () {
      this.bookings = []
    },
    showCreateForm () {
      this.isCreatingBooking = true
    },
    showEditForm (booking) {
      this.isEditingBooking = true
      this.editableBooking = booking
      if (this.editableBooking.leaving === 'Del') {
        this.editableBooking.leaving = true
      } else {
        this.editableBooking.leaving = false
      }
      this.editableBooking.date = new Date(new Date(this.editableBooking.date).toISOString().split('T')[0] + 'T00:00:00')
    },
    async getData () {
      this.isLoading = true
      if (!this.selectedPlant || !this.selectedPlant.id) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      try {
        const data = await CrateBookings.getTable({
          start: this.start.toLocaleDateString('en-CA'),
          end: this.end.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        data.forEach(element => {
          this.bookings.push({
            id: element.id,
            id_site: element.id_site,
            num_crates: element.num_crates,
            status_crates: element.status_crates,
            type: element.status_crates.split('__')[0],
            leaving: element.leaving ? 'Del' : 'Col',
            comment: element.comment,
            title: element.title,
            date: new Date(element.date.split('Z')[0]).toLocaleDateString('en-CA')
          })
        })
        if (this.bookings.length > 0) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.$buefy.toast.open({ message: 'No corresponding bookings found', duration: 3000, type: 'is-danger' })
        }
      } catch (error) {
        this.isLoading = false
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
      this.isLoading = false
    },
    getContextMenuItems (params) {
      const selectedBooking = params.node.data
      let customMenu = []
      const genericMenu = [
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      customMenu.push({
        name: 'Edit Booking',
        action: () => {
          this.showEditForm(selectedBooking)
        },
        icon: '<i class="fas fa-fw fa-edit" ></i>'
      })
      customMenu.push({
        name: 'Delete Booking',
        action: () => {
          this.deleteBooking(selectedBooking)
        },
        icon: '<i class="fas fa-fw fa-xmark" ></i>'
      })
      const result = [...customMenu, ...genericMenu]
      return result
    },
    submitBooking (booking) {
      this.isCreatingBooking = false
      try {
        booking.forEach((element) => {
          CrateBookings.createBooking(element)
        })
        this.$buefy.toast.open({ message: 'Booking created', duration: 3000, type: 'is-success' })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async submitEdit () {
      this.isEditingBooking = false
      this.editableBooking.status_crates = this.editableBooking.type + '__' + this.editableBooking.status_crates.split('__')[1]
      this.editableBooking.date = this.editableBooking.date.toLocaleDateString('en-CA')
      try {
        await CrateBookings.updateBooking(this.editableBooking)
        this.$buefy.toast.open({ message: 'Booking updated', duration: 3000, type: 'is-success' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async deleteBooking (booking) {
      try {
        await CrateBookings.archiveBooking(booking.id)
        this.$buefy.toast.open({ message: 'Booking Deleted', duration: 3000, type: 'is-warning' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>
